/**
 * Sanitize RTE (rich text) HTML for safe display with iframe/video support.
 * Matches behavior from center-content: allowed tags, attributes, and URL schemes.
 */

import DOMPurify from 'isomorphic-dompurify';

const ALLOWED_URI_SCHEMES = ['data', 'https'];
const ALLOWED_URI_REGEX = new RegExp(`^(${ALLOWED_URI_SCHEMES.join('|')}):`, 'im');

// Allowlist: iframe/video/source for embeds, plus img/figure/div/a and common RTE tags
const ALLOWED_TAGS = [
  'iframe',
  'video',
  'source',
  'img',
  'figure',
  'figcaption',
  'div',
  'a',
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'ul',
  'ol',
  'li',
  'span',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'pre',
  'code',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'hr',
];

const IFRAME_ATTRS = [
  'src',
  'width',
  'height',
  'frameborder',
  'allow',
  'allowfullscreen',
  'style',
  'class',
];

const VIDEO_ATTRS = [
  'src',
  'controls',
  'width',
  'height',
  'style',
  'class',
  'autoplay',
  'muted',
  'loop',
];

const SOURCE_ATTRS = ['src', 'type'];

function isAllowedUri(value: string): boolean {
  if (!value || typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  // No scheme = relative URL, always allowed
  if (!trimmed.includes(':')) return true;
  return ALLOWED_URI_REGEX.test(trimmed);
}

/**
 * Sanitize HTML for RTE content with iframe/video support.
 * Allows only specified tags and attributes; restricts URL schemes to data and https.
 */
export function sanitizeRteContent(html: string): string {
  if (typeof html !== 'string') return '';

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.nodeType !== 1) return; // Element nodes only
    const tagName = node.tagName?.toLowerCase?.();

    if (tagName === 'iframe') {
      const allowed = new Set(IFRAME_ATTRS);
      Array.from(node.attributes).forEach((attr) => {
        if (!allowed.has(attr.name.toLowerCase())) {
          node.removeAttribute(attr.name);
        }
      });
      const src = node.getAttribute('src');
      if (src && !isAllowedUri(src)) {
        node.removeAttribute('src');
      }
    } else if (tagName === 'video') {
      const allowed = new Set(VIDEO_ATTRS);
      Array.from(node.attributes).forEach((attr) => {
        if (!allowed.has(attr.name.toLowerCase())) {
          node.removeAttribute(attr.name);
        }
      });
      const src = node.getAttribute('src');
      if (src && !isAllowedUri(src)) {
        node.removeAttribute('src');
      }
    } else if (tagName === 'source') {
      const allowed = new Set(SOURCE_ATTRS);
      Array.from(node.attributes).forEach((attr) => {
        if (!allowed.has(attr.name.toLowerCase())) {
          node.removeAttribute(attr.name);
        }
      });
      const src = node.getAttribute('src');
      if (src && !isAllowedUri(src)) {
        node.removeAttribute('src');
      }
    }

    // Any other node: enforce allowed schemes for href/src too
    if (node.hasAttribute('href')) {
      const href = node.getAttribute('href');
      if (href && !isAllowedUri(href)) {
        node.removeAttribute('href');
      }
    }
    if (node.hasAttribute('src') && !['iframe', 'video', 'source'].includes(tagName ?? '')) {
      const src = node.getAttribute('src');
      if (src && !isAllowedUri(src)) {
        node.removeAttribute('src');
      }
    }
  });

  try {
    const clean = DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ADD_ATTR: [...IFRAME_ATTRS, ...VIDEO_ATTRS, ...SOURCE_ATTRS],
    });
    return clean;
  } catch {
    return html;
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes');
  }
}
